import React, { useRef, useState, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TiptapImage from '@tiptap/extension-image';
import { Box, IconButton, Divider, Tooltip } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import ImageIcon from '@mui/icons-material/Image';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB limit
const MAX_IMAGE_WIDTH = 1600; // Max width in pixels
const MAX_IMAGE_HEIGHT = 1600; // Max height in pixels
const MIN_RESIZE_DIMENSION = 50; // Min resize width/height in pixels

const ResizableImage = TiptapImage.extend({
  addNodeView() {
    const parentNodeView = this.parent?.();
    if (!parentNodeView) return null;

    return (props) => {
      const nodeView = parentNodeView(props);
      if (!nodeView) return null;

      const { element: el, dom } = nodeView;
      el.draggable = false;

      const showNodeView = () => {
        dom.style.visibility = '';
        dom.style.pointerEvents = '';
      };

      if (el.complete) {
        showNodeView();
      } else {
        el.onload = showNodeView;
        el.onerror = showNodeView;
      }

      const originalOnCommit = nodeView.onCommit;
      nodeView.onCommit = (width, height) => {
        const resolvedWidth = Math.round(width || parseFloat(el.style.width) || 0);
        const resolvedHeight = Math.round(height || parseFloat(el.style.height) || 0);
        if (resolvedWidth > 0 && resolvedHeight > 0) {
          originalOnCommit(resolvedWidth, resolvedHeight);
        }
      };

      const originalOnUpdate = nodeView.onUpdate;
      nodeView.onUpdate = (updatedNode, decorations, innerDecorations) => {
        if (originalOnUpdate && !originalOnUpdate(updatedNode, decorations, innerDecorations)) {
          return false;
        }
        if (updatedNode.attrs.src && el.getAttribute('src') !== updatedNode.attrs.src) {
          el.src = updatedNode.attrs.src;
        }
        el.style.width = updatedNode.attrs.width ? `${updatedNode.attrs.width}px` : '';
        el.style.height = updatedNode.attrs.height ? `${updatedNode.attrs.height}px` : '';
        return true;
      };

      return nodeView;
    };
  },
});

/**
 * Resize image to fit within max dimensions while preserving aspect ratio
 * Returns base64 data URL for immediate display in editor
 */
const resizeImage = (file, maxWidth, maxHeight) => {
  return new Promise((resolve) => {
    // Skip resizing for GIFs to preserve animation
    if (file.type === 'image/gif') {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result);
      reader.readAsDataURL(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;

        // Calculate new dimensions maintaining aspect ratio
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to base64 (use original format, fallback to jpeg)
        const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        const quality = 0.85; // 85% quality for jpeg
        resolve(canvas.toDataURL(outputType, quality));
      };
      img.src = e.target?.result;
    };
    reader.readAsDataURL(file);
  });
};

const validateAndResizeImage = async (file) => {
  // Validate file type
  if (!file.type.startsWith('image/')) {
    alert('Please select an image file');
    return null;
  }

  // Validate file size
  if (file.size > MAX_FILE_SIZE) {
    alert('Image must be less than 5MB');
    return null;
  }

  // Resize image and convert to base64 for immediate display
  // The base64 will be replaced with GCS URL when the note is saved
  return resizeImage(file, MAX_IMAGE_WIDTH, MAX_IMAGE_HEIGHT);
};

const insertImagesFromFiles = async (files, targetEditor, insertPos = null) => {
  if (!targetEditor || !files?.length) return;

  let nextPos = insertPos;
  for (const file of Array.from(files)) {
    const base64 = await validateAndResizeImage(file);
    if (base64) {
      const chain = targetEditor.chain().focus();
      if (typeof nextPos === 'number') {
        chain.setTextSelection(nextPos);
        nextPos = null;
      }
      chain.setImage({ src: base64 }).run();
    }
  }
};

const hasDraggedFiles = (event) => Array.from(event.dataTransfer?.types || []).includes('Files');

const MenuBar = ({ editor }) => {
  const fileInputRef = useRef(null);

  if (!editor) return null;

  const handleImageUpload = async (event) => {
    const files = event.target.files;
    if (!files?.length) return;

    await insertImagesFromFiles(files, editor);

    // Reset input so the same file can be selected again
    event.target.value = '';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        borderBottom: '1px solid #E0E0E0',
        pb: 1,
        mb: 1,
      }}
    >
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleBold().run()}
        sx={{
          backgroundColor: editor.isActive('bold') ? '#E8E8E8' : 'transparent',
          borderRadius: '4px',
        }}
      >
        <FormatBoldIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        sx={{
          backgroundColor: editor.isActive('italic') ? '#E8E8E8' : 'transparent',
          borderRadius: '4px',
        }}
      >
        <FormatItalicIcon fontSize="small" />
      </IconButton>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        sx={{
          backgroundColor: editor.isActive('bulletList') ? '#E8E8E8' : 'transparent',
          borderRadius: '4px',
        }}
      >
        <FormatListBulletedIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        sx={{
          backgroundColor: editor.isActive('orderedList') ? '#E8E8E8' : 'transparent',
          borderRadius: '4px',
        }}
      >
        <FormatListNumberedIcon fontSize="small" />
      </IconButton>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImageUpload}
        accept="image/*,.gif"
        style={{ display: 'none' }}
      />
      <Tooltip title="Add image or GIF">
        <IconButton size="small" onClick={() => fileInputRef.current?.click()}>
          <ImageIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      <IconButton size="small" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
        <UndoIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
        <RedoIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

/**
 * Rich Text Editor for Release Notes
 *
 * Images are inserted as base64 for immediate display.
 * When the note is saved, the backend extracts base64 images,
 * uploads them to GCS, and replaces with GCS URLs.
 *
 * @param {string} value - HTML content
 * @param {function} onChange - Callback when content changes
 * @param {string} placeholder - Placeholder text
 */
const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: placeholder || 'Start typing...',
      }),
      ResizableImage.configure({
        inline: false,
        allowBase64: true,
        resize: {
          enabled: true,
          directions: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
          minWidth: MIN_RESIZE_DIMENSION,
          minHeight: MIN_RESIZE_DIMENSION,
          alwaysPreserveAspectRatio: true,
        },
      }),
    ],
    content: value,
    editorProps: {
      handleDrop: (view, event, _slice, moved) => {
        if (moved) return false;

        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) return false;

        event.preventDefault();
        event.stopPropagation();
        setIsDraggingOver(false);

        const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY });
        insertImagesFromFiles(files, editorRef.current, coordinates?.pos ?? null);
        return true;
      },
      handlePaste: (_view, event) => {
        const files = event.clipboardData?.files;
        if (!files || files.length === 0) return false;

        const imageFiles = Array.from(files).filter((file) => file.type.startsWith('image/'));
        if (imageFiles.length === 0) return false;

        event.preventDefault();
        insertImagesFromFiles(imageFiles, editorRef.current);
        return true;
      },
    },
    onUpdate: ({ editor: updatedEditor }) => {
      onChange(updatedEditor.getHTML());
    },
  });

  editorRef.current = editor;

  // Update editor content when value changes externally (e.g., when editing)
  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  const handleDragEnter = useCallback((event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    setIsDraggingOver(true);
  }, []);

  const handleDragOver = useCallback((event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    setIsDraggingOver(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setIsDraggingOver(false);
  }, []);

  const handleContainerDrop = useCallback((event) => {
    setIsDraggingOver(false);
    if (event.defaultPrevented) return;

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    event.preventDefault();
    event.stopPropagation();
    insertImagesFromFiles(files, editorRef.current);
  }, []);

  return (
    <Box
      data-testid="rich-text-editor-dropzone"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleContainerDrop}
      sx={{
        border: isDraggingOver ? '2px dashed #772EDD' : '1px solid #C4C4C4',
        backgroundColor: isDraggingOver ? 'rgba(119, 46, 221, 0.04)' : 'transparent',
        borderRadius: '4px',
        p: 1.5,
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
        '&:hover': {
          borderColor: isDraggingOver ? '#772EDD' : '#000',
        },
        '&:focus-within': {
          borderColor: '#772EDD',
          borderWidth: '2px',
        },
        '.ProseMirror': {
          outline: 'none',
          minHeight: '200px',
          fontSize: '14px',
          lineHeight: '1.5',
          '& p': {
            margin: 0,
          },
          '& ul, & ol': {
            paddingLeft: '20px',
            margin: '8px 0',
          },
          '& li': {
            marginBottom: '4px',
          },
          '& [data-resize-container]': {
            margin: '8px 0',
            maxWidth: '100%',
          },
          '& [data-resize-wrapper]': {
            display: 'inline-block',
            lineHeight: 0,
            maxWidth: '100%',
            borderRadius: '4px',
            transition: 'outline 0.15s ease',
            '&:hover': {
              outline: '2px solid #772EDD',
            },
            '&:hover [data-resize-handle]': {
              opacity: 1,
            },
          },
          '& .ProseMirror-selectednode [data-resize-wrapper], & [data-resize-container][data-resize-state="true"] [data-resize-wrapper]':
            {
              outline: '2px solid #772EDD',
              '& [data-resize-handle]': {
                opacity: 1,
              },
            },
          '& [data-resize-handle]': {
            width: '10px',
            height: '10px',
            backgroundColor: '#772EDD',
            border: '1.5px solid #FFFFFF',
            borderRadius: '2px',
            boxSizing: 'border-box',
            opacity: 0,
            transition: 'opacity 0.15s ease',
            zIndex: 2,
          },
          '& [data-resize-handle="top-left"]': {
            top: '-5px !important',
            left: '-5px !important',
            cursor: 'nwse-resize',
          },
          '& [data-resize-handle="top-right"]': {
            top: '-5px !important',
            right: '-5px !important',
            cursor: 'nesw-resize',
          },
          '& [data-resize-handle="bottom-left"]': {
            bottom: '-5px !important',
            left: '-5px !important',
            cursor: 'nesw-resize',
          },
          '& [data-resize-handle="bottom-right"]': {
            bottom: '-5px !important',
            right: '-5px !important',
            cursor: 'nwse-resize',
          },
          '& img': {
            maxWidth: '100%',
            height: 'auto !important',
            borderRadius: '4px',
            display: 'block',
          },
        },
        '.ProseMirror p.is-editor-empty:first-child::before': {
          content: 'attr(data-placeholder)',
          color: '#9E9E9E',
          float: 'left',
          height: 0,
          pointerEvents: 'none',
        },
      }}
    >
      <MenuBar editor={editor} />
      <EditorContent editor={editor} />
    </Box>
  );
};

export default RichTextEditor;
